import '@mantine/carousel/styles.css';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { Button } from '@mantine/core';
import { useCountdown } from './common/useCountdown';
import { useLogout } from 'src/auth/hooks/useLogout';
import { RedirectToLoginPage } from './auth/RedirectToLoginPage';
import ClientsPage from './clients/ClientsPage';
import DashboardPage from './dashboard/DashboardPage';
import CreateFamilyPage from './clients/CreateFamilyV2/CreateFamilyPage';
import EditFamilyPage from './clients/CreateFamilyV2/EditFamilyPage';
import AddFamilyMemberPage from './clients/CreateFamilyV2/AddFamilyMemberPage';
import RedirectUnknownClient from './clients/RedirectUnknownClient';
import { useIsAfternoon } from './common/useIsAfternoonHook';
import { useFlags, useFlagsReady } from './feature-flags/FlagsHook';
import { ClientRootRedirect } from './clients/ClientRootRedirect';
import DetectedEntitiesPage from './data-capture/detected-entities/DetectedEntitiesPage';
import { DebugPage } from './debug/DebugPage';
import ViewEntityPage from './financial-data/ViewEntityPage';
import ClientData from './home/ClientData';
import { FamilyData } from 'src/non-financial-data/v2/family/FamilyData';
import AssignSpeakerPage from 'src/non-financial-data/v2/family/AssignSpeakerPage';
import MeetingDetailsScreen from 'src/non-financial-data/v2/family/MeetingDetailsScreen';
import { CompleteConnectionFlowPage } from 'src/financial/connections/CompleteConnectionFlowPage';
import SecretMenuPage from './secret-menu/SecretMenuPage';
import { AppContainer, RoutePane } from './UI/app-container';
import PageLoader from 'src/UI/loader/PageLoader';
import CreateFinancialItemPage from 'src/financial/financial-data/financial-item/CreateFinancialItemPage';
import ViewAndEditFinancialItemPage from 'src/financial/financial-data/financial-item/ViewAndEditFinancialItemPage';
import AdminPage from './admin/AdminPage';
import { SessionData, useSessionData } from './auth/SessionDataHook';
import useCurrentUser from './admin/users/hooks/useCurrentUser';
import UserDetailsPage from './user/UserDetailsPage';
import { GenericErrorPage } from './error/GenericErrorPage';
import CompanyPage from './company/CompanyPage';
import CompanyFilesPage from 'src/company/documents/components/CompanyFilesPage';
import AddCompanyFilesPage from 'src/company/documents/components/AddCompanyFilesPage';
import FundDetailPage from 'src/company/funds/components/FundDetailPage';
import MatchingWorkspacePage from 'src/connected-assets/matching/MatchingWorkspacePage';
import { OAuthRedirectPage } from 'src/meetings/oauth-redirect/OAuthRedirectPage';
import { MeetingsPage } from 'src/meetings/home/MeetingsPage';
import { MeetingDetailPage } from 'src/meetings/detail/MeetingDetailPage';
import SuitabilityReportEditPage from 'src/suitability-report/edit/SuitabilityReportEditPage';
import MeetingAgendaEditPage from 'src/meeting-agenda/edit/MeetingAgendaEditPage';
import DataExportEditPage from './data-export/edit/DataExportEditPage';
import NewSuitabilityFlow from './suitability-report/NewSuitabilityFlow';

// Lazy so echarts ships in an async chunk only modelling users download. The
// content pane already provides the Suspense boundary.
const CashflowModelPage = lazy(() => import('./cashflows/modelling/CashflowModelPage'));
import AgendaFlowPage from './meeting-agenda/AgendaFlowPage';
import MeetingNoteEditPage from './meeting-note/edit/MeetingNoteEditPage';
import MeetingNoteFlowPage from './meeting-note/MeetingNoteFlowPage';
import DataExportFlowPage from './data-export/DataExportFlowPage';
import OnboardingPage from 'src/onboarding/OnboardingPage';
import OnboardingSignupPage from 'src/onboarding-signup/OnboardingSignupPage';
import { InvitePageRoute } from 'src/auth/invite/InvitePage';
import { AcceptLinkPage } from 'src/auth/accept-link/AcceptLinkPage';
import { LoginPage } from 'src/auth/login/LoginPage';
import { DocsOAuthRedirectPage } from 'src/secret-menu/sharepoint/DocsOAuthRedirectPage';
import { MicrosoftEmailOAuthCallbackPage } from 'src/secret-menu/microsoft-email/MicrosoftEmailOAuthCallbackPage';
import { LogoutPage } from 'src/auth/logout/LogoutPage';
import { RecoveryPage } from 'src/auth/recovery/RecoveryPage';
import { SettingsPageRoute } from 'src/auth/settings/SettingsPage';
import { SsoMismatchPage } from 'src/auth/sso-mismatch/SsoMismatchPage';
import { SuccessMessage } from 'src/auth/components/SuccessMessage';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { AccountDeletionPage } from 'src/auth/deletion/AccountDeletionPage';
import { lazy, Suspense } from 'react';

function isRecoverySession(session: SessionData): boolean {
  const authMethods = session.authentication_methods ?? [];
  return authMethods.some(m => m.method === 'code_recovery');
}

export default function AllRoutesNew() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/join" element={<OnboardingPage />} />
        <Route path="/onboarding/:token" element={<OnboardingInviteGate />} />
        <Route path="/onboarding" element={<OnboardingInviteGate />} />
        <Route path="/onboarding/*" element={<OnboardingInviteGate />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-link" element={<AcceptLinkPage />} />
        <Route path="/invite" element={<InvitePageRoute />} />
        <Route path="/recovery" element={<RecoveryPage />} />
        <Route path="/settings" element={<SettingsPageRoute />} />
        <Route path="/sso-mismatch" element={<SsoMismatchPage />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/account-deletion" element={<AccountDeletionPage />} />
        <Route path="*" element={<ProtectedRoutesWithSessionNew />} />
      </Routes>
    </Suspense>
  );
}

// Registered unconditionally: while the flags are loading they read as off, so a
// conditionally-registered route would be absent and drop an invited prospect into
// the protected-route branch. The route always exists; only /onboarding visitors
// wait on LaunchDarkly, and they wait for the real flag value.
function OnboardingInviteGate() {
  const flags = useFlags();
  const flagsReady = useFlagsReady();

  if (!flagsReady) return <PageLoader />;
  if (!flags.flagEnabledOnboardingInvite) return <RedirectToLoginPage />;
  return <OnboardingSignupPage />;
}

function ProtectedRoutesWithSessionNew() {
  const { isLoading, session } = useSessionData();

  if (isLoading) return <PageLoader />;
  if (!session) return <RedirectToLoginPage />;

  const hasRecoverySession = isRecoverySession(session);
  if (hasRecoverySession) {
    return <RecoverySessionInterstitial />;
  }

  return <ProtectedRoutesWithUserCheck />;
}

function RecoverySessionInterstitial() {
  const handleGoToLogin = () => {
    window.location.href = '/logout';
  };

  return (
    <AuthPageLayout>
      <SuccessMessage variant="sso_linked" onAction={handleGoToLogin} />
    </AuthPageLayout>
  );
}

function AdminRoute() {
  const { isSystemAdmin, isServiceAdmin, isLoading } = useCurrentUser();
  const location = useLocation();

  if (isLoading) return <PageLoader />;

  // Service admins may only manage adviser signatures from the users tab; AdminPage enforces per-tab access.
  if (!isSystemAdmin && !isServiceAdmin) {
    const fallback = location.state?.from?.pathname || '/';
    return <Navigate to={fallback} replace />;
  }

  return <Outlet />;
}

function ProtectedRoutesWithUserCheck() {
  const { currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const { mutate: logout } = useLogout({
    onError: () => {
      window.location.replace('/login');
    },
  });

  const isUserInactive =
    !isLoadingUser && currentUser && !['ACTIVE', 'PENDING'].includes(currentUser.userStatus);
  const countdown = useCountdown(30, !!isUserInactive, () => logout());

  if (isLoadingUser) return <PageLoader />;

  if (!currentUser) {
    return <GenericErrorPage error="Current user is not available. Please try logging in again." />;
  }

  if (!['ACTIVE', 'PENDING'].includes(currentUser.userStatus)) {
    return (
      <GenericErrorPage
        error="Your account is not active. Please contact your system administrator."
        title="Account inactive"
        text={
          <>
            Your account is not active. Please contact your system administrator. You will be logged
            out in{' '}
            <span
              style={{
                display: 'inline-block',
                minWidth: '2ch',
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {countdown}
            </span>{' '}
            seconds.
          </>
        }
        showRefresh={false}
        actions={
          <Button variant="filled" size="md" onClick={() => logout()}>
            Log out now
          </Button>
        }
      />
    );
  }

  return <ProtectedRoutes />;
}

function ProtectedRoutes() {
  const { isAfternoon, isAfternoonAdmin } = useIsAfternoon();
  const {
    flagEnabledDocumentEdit,
    flagEnabledFamilyFlowFrontend,
    flagEnabledIndividualDetailsRework,
    flagEnabledFamilyScreenRework,
    flagEnabledFamilyDocumentsRevamp,
    flagEnabledMeetingsRework,
    flagEnabledHomeDashboard,
  } = useFlags();

  return (
    <Routes>
      <Route path="/" element={<AppContainer />}>
        <Route
          index
          element={
            <Navigate to={flagEnabledHomeDashboard ? '/dashboard' : '/clients/all'} replace />
          }
        />
        {flagEnabledHomeDashboard && (
          <Route element={<RoutePane bg="gray.0" p="xxl" />}>
            <Route path="dashboard" element={<DashboardPage />} />
          </Route>
        )}
        {flagEnabledFamilyFlowFrontend && (
          <Route element={<RoutePane bg="white" p={0} />}>
            <Route path="clients/families/new" element={<CreateFamilyPage />} />
            <Route path="clients/families/:id/edit" element={<EditFamilyPage />} />
          </Route>
        )}
        {flagEnabledIndividualDetailsRework ? (
          <Route element={<RoutePane bg="white" p="xxl" />}>
            <Route path="clients/:tab/:id?" element={<ClientsPage />} />
          </Route>
        ) : (
          <Route path="clients/:tab/:id?" element={<ClientsPage />} />
        )}
        {flagEnabledIndividualDetailsRework && flagEnabledFamilyScreenRework ? (
          <>
            <Route element={<RoutePane bg="white" p="xxl" />}>
              <Route
                path="family/:familyId/:tab/:sectionSlug/folder/:folderId"
                element={<FamilyData />}
              />
              <Route path="family/:familyId/:tab/:sectionSlug/:itemId" element={<FamilyData />} />
              <Route path="family/:familyId/:tab?/:sectionSlug?" element={<FamilyData />} />
            </Route>
            <Route element={<RoutePane bg="gray.1" p={0} />}>
              <Route
                path="family/:familyId/cashflows/model/:scenarioId/:area?"
                element={<CashflowModelPage scope="family" />}
              />
            </Route>
            <Route element={<RoutePane bg="white" p={0} />}>
              <Route path="family/:familyId/edit" element={<EditFamilyPage />} />
              <Route path="family/:familyId/add-member" element={<AddFamilyMemberPage />} />
              {flagEnabledMeetingsRework && (
                <>
                  <Route
                    path="family/:familyId/meeting/:eventId"
                    element={<MeetingDetailsScreen />}
                  />
                  <Route
                    path="family/:familyId/meeting/:eventId/:eventTab"
                    element={<MeetingDetailsScreen />}
                  />
                  <Route
                    path="family/:familyId/meetings/:eventId/speakers/:speakerLabel"
                    element={<AssignSpeakerPage />}
                  />
                </>
              )}
            </Route>
          </>
        ) : (
          <Route
            path="family/:familyId/:tab?"
            element={<Navigate to="/clients/families" replace />}
          />
        )}
        <Route path="client/undefined" element={<RedirectUnknownClient />} />

        {flagEnabledDocumentEdit && (
          <Route element={<RoutePane bg="white" />}>
            <Route
              path="client/:clientId/document/suitability-reports/:id/edit"
              element={<SuitabilityReportEditPage />}
            />
            <Route
              path="client/:clientId/document/data-exports/:id/edit"
              element={<DataExportEditPage />}
            />
            <Route
              path="client/:clientId/document/meeting-agendas/:id/edit"
              element={<MeetingAgendaEditPage />}
            />
          </Route>
        )}

        <Route element={<RoutePane bg="white" />}>
          <Route
            path="client/:clientId/document/meeting-notes/:id/edit"
            element={<MeetingNoteEditPage />}
          />
        </Route>

        {flagEnabledIndividualDetailsRework &&
          flagEnabledFamilyScreenRework &&
          flagEnabledFamilyDocumentsRevamp &&
          flagEnabledDocumentEdit && (
            <Route element={<RoutePane bg="white" />}>
              <Route
                path="family/:familyId/documents/suitability-reports/:id/edit"
                element={<SuitabilityReportEditPage />}
              />
              <Route
                path="family/:familyId/documents/data-exports/:id/edit"
                element={<DataExportEditPage />}
              />
              <Route
                path="family/:familyId/documents/meeting-agendas/:id/edit"
                element={<MeetingAgendaEditPage />}
              />
            </Route>
          )}

        {flagEnabledIndividualDetailsRework &&
          flagEnabledFamilyScreenRework &&
          flagEnabledFamilyDocumentsRevamp && (
            <Route element={<RoutePane bg="white" />}>
              <Route
                path="family/:familyId/documents/meeting-notes/:id/edit"
                element={<MeetingNoteEditPage />}
              />
            </Route>
          )}

        <Route element={<RoutePane bg="white" skipDefaultSidebar />}>
          <Route path="client/:clientId/suitability-report" element={<NewSuitabilityFlow />} />
        </Route>

        <Route element={<RoutePane bg="white" skipDefaultSidebar />}>
          <Route path="client/:clientId/meeting-agenda" element={<AgendaFlowPage />} />
          <Route path="client/:clientId/meeting-note" element={<MeetingNoteFlowPage />} />
          <Route path="client/:clientId/data-export" element={<DataExportFlowPage />} />
        </Route>

        {flagEnabledMeetingsRework && (
          <Route element={<RoutePane bg="white" p={0} />}>
            <Route path="client/:clientId/meeting/:eventId" element={<MeetingDetailsScreen />} />
            <Route
              path="client/:clientId/meeting/:eventId/:eventTab"
              element={<MeetingDetailsScreen />}
            />
            <Route
              path="client/:clientId/meetings/:eventId/speakers/:speakerLabel"
              element={<AssignSpeakerPage />}
            />
          </Route>
        )}

        <Route path="client/:clientId">
          <Route index element={<ClientRootRedirect />} />
          <Route element={<RoutePane bg="gray.1" p={0} />}>
            <Route
              path="cashflows/model/:scenarioId/:area?"
              element={<CashflowModelPage scope="client" />}
            />
          </Route>
          <Route path=":tab/:sectionSlug/folder/:folderId" element={<ClientData />} />
          <Route path=":tab/:sectionSlug/:selectedEventId" element={<ClientData />} />
          <Route path=":tab/:sectionSlug/:selectedEventId/:eventTab" element={<ClientData />} />
          <Route path=":tab/:sectionSlug?" element={<ClientData />} />
          <Route element={<RoutePane bg="white" sidebarCollapsed />}>
            <Route path="detected-entities" element={<DetectedEntitiesPage />} />
            <Route path="financial-items/create/:product" element={<CreateFinancialItemPage />} />
            <Route
              path="financial-items/:product/:id/"
              element={<ViewAndEditFinancialItemPage />}
            />
          </Route>
        </Route>

        <Route element={<RoutePane sidebarCollapsed />}>
          <Route path="user/:tab/:id?" element={<UserDetailsPage />} />
        </Route>

        <Route path="company/connected-assets/matching" element={<MatchingWorkspacePage />} />
        <Route path="company/documents/add" element={<AddCompanyFilesPage />} />
        <Route path="company/documents/folder/:folderId" element={<CompanyFilesPage />} />
        <Route path="company/documents" element={<CompanyFilesPage />} />
        <Route path="company/:tab?/" element={<CompanyPage />} />
        <Route path="company/:tab?/:fundId?" element={<FundDetailPage />} />

        <Route path="admin" element={<AdminRoute />}>
          <Route path=":tab/:id?" element={<AdminPage />} />
        </Route>

        <Route path="/complete-connection-flow" element={<CompleteConnectionFlowPage />} />

        <Route element={<RoutePane sidebarCollapsed />}>
          <Route
            path="/oauth-callback/google-calendar"
            element={<OAuthRedirectPage platform="GOOGLE" />}
          />
          <Route
            path="/oauth-callback/microsoft-calendar"
            element={<OAuthRedirectPage platform="MICROSOFT" />}
          />
          <Route
            path="/oauth-callback/microsoft-docs"
            element={<DocsOAuthRedirectPage platform="MICROSOFT" />}
          />
          <Route
            path="/oauth-callback/microsoft-email"
            element={<MicrosoftEmailOAuthCallbackPage />}
          />
        </Route>

        {flagEnabledMeetingsRework ? (
          <>
            <Route element={<RoutePane bg="white" p={0} sidebarCollapsed />}>
              <Route path="/meetings" element={<MeetingsPage />} />
            </Route>
            <Route element={<RoutePane bg="white" p={0} />}>
              <Route path="/meetings/:eventId" element={<MeetingDetailsScreen />} />
              <Route path="/meetings/:eventId/:eventTab" element={<MeetingDetailsScreen />} />
              <Route
                path="/meetings/:eventId/speakers/:speakerLabel"
                element={<AssignSpeakerPage />}
              />
            </Route>
          </>
        ) : (
          <>
            <Route element={<RoutePane bg="white" p={0} sidebarCollapsed />}>
              <Route path="/meetings" element={<MeetingsPage />} />
            </Route>
            <Route element={<RoutePane bg="white" p="xxl" sidebarCollapsed />}>
              <Route path="/meetings/:eventId" element={<MeetingDetailPage />} />
              <Route path="/meetings/:eventId/:eventTab" element={<MeetingDetailPage />} />
            </Route>
          </>
        )}

        <Route path="/view-entity" element={<ViewEntityPage />} />

        {isAfternoon && <Route path="/debug" element={<DebugPage />} />}
        {isAfternoonAdmin && (
          <Route element={<RoutePane sidebarCollapsed />}>
            <Route path="secret-menu/:tab?/" element={<SecretMenuPage />} />
          </Route>
        )}
      </Route>
    </Routes>
  );
}
